import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Layout, Menu, Tooltip, Button
} from 'antd';
import {
  DashboardOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  LogoutOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  BarChartOutlined,
  BarcodeOutlined
} from '@ant-design/icons';

import Logo from '../assets/images/logo.png';
import UserBox from '../components/shared/UserBox';
import Dashboard from '../components/tabs/Dashboard';
import Users from '../components/tabs/Users';
import Attendance from '../components/tabs/Attendance';
import useFullScreen from '../hooks/useFullScreen';
import ApiService from '../utils/apiService';
import { isXrole, removeSessionAndLogoutUser } from '../utils/authentication';
import notificationWithIcon from '../utils/notification';
import ReportAttendance from '../components/tabs/ReportAttendance';
import TicketsPage from '../components/tabs/Ticket';

const {
  Header, Content, Footer, Sider
} = Layout;

function Main() {
  const { isFullscreen, toggleFullScreen } = useFullScreen();
  const [selectedKeys, setSelectedKeys] = useState('1');
  const navigate = useNavigate();
  const { tab } = useParams();

  /** Logout utilisateur */
  const userLogout = async () => {
    try {
      const response = await ApiService.post('/api/v_1/create/by_emia/from_tana/auth/logout');
      if (response?.result_code === 0) {
        removeSessionAndLogoutUser();
      } else {
        notificationWithIcon('error', 'ERROR', 'Server error');
        removeSessionAndLogoutUser();
      }
    } catch (error) {
      notificationWithIcon(
        'error',
        'ERROR',
        error?.response?.data?.result?.error || 'Server error'
      );
      removeSessionAndLogoutUser();
    }
  };

  /** Changement d'onglet */
  const handleTabChange = (key) => {
    switch (key) {
      case '0':
        navigate('/main/accueil');
        break;
      case '1':
        navigate('/main/dashboard');
        break;
      case '2':
        navigate('/main/users');
        break;
      case '3':
        navigate('/main/attendance');
        break;
      case '4':
        navigate('/main/report-attendance');
        break;
      case '5':
        navigate('/main/ticket');
        break;
      case '10':
        userLogout();
        break;
      default:
        navigate('/main/accueil');
    }
  };

  /** Synchroniser selectedKeys avec l'URL */
  useEffect(() => {
    if (!tab) return;

    switch (tab) {
      case 'accueil':
        setSelectedKeys('0');
        break;
      case 'dashboard':
        setSelectedKeys('1');
        break;
      case 'users':
        setSelectedKeys('2');
        break;
      case 'attendance':
        setSelectedKeys('3');
        break;
      case 'report-attendance':
        setSelectedKeys('4');
        break;
      case 'ticket':
        setSelectedKeys('5');
        break;
      default:
        navigate('/not-found');
    }
  }, [tab, navigate]);

  /** Titres dynamiques */
  const titles = {
    0: 'Accueil - Administrateur Eglise Emia',
    1: 'Tableau de bord - Administrateur Eglise Emia',
    2: 'Membres - Administrateur Eglise Emia',
    3: 'Présence - Administrateur Eglise Emia'
  };

  useEffect(() => {
    const admin = isXrole('ADMIN_ROLE');

    if (!admin && selectedKeys !== '10') {
      window.document.title = 'Dashboard - Administrateur Eglise Emia';
      return;
    }

    window.document.title = titles[selectedKeys] || 'Dashboard - Administrateur Eglise Emia';
  }, [selectedKeys]);

  /** Menu items */
  const menuItems = [
    { key: '0', icon: <HomeOutlined />, label: 'Accueil' },
    { key: '1', icon: <DashboardOutlined />, label: 'Tableau de bord' },
    { key: '2', icon: <TeamOutlined />, label: 'Membres' },
    { key: '3', icon: <ClockCircleOutlined />, label: 'Présence' },
    { key: '4', icon: <BarChartOutlined />, label: 'Rapport de présence' },
    { key: '5', icon: <BarcodeOutlined />, label: 'Billet' },
    { key: '10', icon: <LogoutOutlined />, label: 'Se déconnecter' }
  ];

  const filteredItems = isXrole('ADMIN_ROLE') ?
    menuItems :
    menuItems.filter((item) => item.key === '10' || item.key === '2' || item.key === '5'); // les autres voient seulement "Se déconnecter"

  return (
    <Layout className='w-full h-screen'>
      <Sider width={250} breakpoint='lg' collapsedWidth='0'>
        <UserBox />
        <Menu
          theme='dark'
          mode='inline'
          selectedKeys={[selectedKeys]}
          onClick={(e) => handleTabChange(e.key)}
          items={filteredItems}
        />
      </Sider>

      <Layout>
        <Header className='p-0 !bg-bg-white'>
          <Link to='/main/accueil'>
            <img className='h-[65px] mx-auto' alt='logo' src={Logo} />
          </Link>

          <Tooltip title='Click to toggle Full Screen' placement='left'>
            <Button
              className='absolute right-5 top-5'
              icon={
                isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
              }
              onClick={toggleFullScreen}
              shape='default'
              type='default'
              size='middle'
            />
          </Tooltip>
        </Header>

        <Content className='bg-bg-white overflow-y-scroll m-2 p-2'>
          {selectedKeys === '1' && isXrole('ADMIN_ROLE') && <Dashboard />}
          {selectedKeys === '2' && <Users />}
          {selectedKeys === '3' && isXrole('ADMIN_ROLE') && <Attendance />}
          {selectedKeys === '4' && isXrole('ADMIN_ROLE') && <ReportAttendance />}
          {selectedKeys === '5' && <TicketsPage />}
        </Content>

        <Footer className='hidden text-center font-text-font font-medium'>
          ©2025 Developed By
          {' '}
          {isXrole('ADMIN_ROLE') ? (
            <a
              className='text-color-primary hover:text-color-secondary'
              href='https://josiayvan.github.io/baiboly/src/view/'
              target='_blank'
              rel='noreferrer'
            >
              Mr. Josia Yvan
            </a>
          ) : (
            <span
              className='text-color-primary hover:text-color-secondary'
            >
              Mr. Josia Yvan
            </span>
          )}
        </Footer>
      </Layout>
    </Layout>
  );
}

export default React.memo(Main);
